import styled from '@emotion/styled';
import { Box, Space } from '@stenajs-webui/core';
import { Cardy } from '@stenajs-webui/elements';
import { type FC, type ReactNode, useEffect } from 'react';
import { useHasAnyValidCustomer } from '../../../../../common/graphql/useHasAnyValidCustomer';
import { useUpdateFormState } from '../../../../../common/update-information/hooks/useUpdateFormState';
import { UPDATE_SHOW } from '../../../../../common/update-information/utils/getUpdateSubmitInformation';
import { getFieldsToShow, isAnyVisible } from '../../../../../common/utils/bookingFields';
import { transformUpdateBookingFormStateToMinimalBooking } from '../../../../../common/utils/minimalBooking';
import {
  BookingColumn,
  BookingColumnGrid,
} from '../../../../../common-ui/layout/BookingColumnGrid';
import { NoSailingSelected } from '../../../../../common-ui/sailing-info/NoSailingSelected';
import { SailingInfoHorizontal } from '../../../../../common-ui/sailing-info/SailingInfoHorizontal';
import { ColumnId } from '../../../../../gql/graphql';
import type { UpdateFormIdVariant } from '../../../../../services/update-form/updateFormReducer';
import { breakpoints } from '../../../../../themes/defaultTheme';
import { getFieldsToEdit } from '../../utils/detailsUtils';
import { CargoGoodsDetailsSection } from '../sections/CargoGoodsDetailsSection';
import { ImportExportDetailsSection } from '../sections/ImportExportDetailsSection';
import { LoadingDetailsSection } from '../sections/LoadingDetailsSection';
import { SailingDetailsSection } from '../sections/SailingDetailsSection';
import { VehicleDetailsSection } from '../sections/VehicleDetailsSection';
import { BookingDetailsEditBanners } from './BookingDetailsEditBanners';

export interface BookingDetailsEditContentProps {
  bookingNo: number;
  children?: ReactNode;
  onStopEdit: () => void;
}

export const DimensionsFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: calc(2 * var(--swui-metrics-space));

  @media (min-width: ${breakpoints.md}) {
    grid-auto-flow: row;
  }
`;

const formId: UpdateFormIdVariant = 'Details';

export const BookingDetailsEditContent: FC<BookingDetailsEditContentProps> = ({
  bookingNo,
  onStopEdit,
  children,
}) => {
  const { booking, formState, submitting, updateSubmitInformation } = useUpdateFormState({
    bookingNo,
    formId,
  });

  const hasAnyValidCustomer = useHasAnyValidCustomer();

  useEffect(() => {
    if (
      updateSubmitInformation.status === UPDATE_SHOW.SUCCESS ||
      updateSubmitInformation.status === UPDATE_SHOW.INFORMATION
    ) {
      onStopEdit();
    }
  }, [onStopEdit, updateSubmitInformation]);

  if (!booking || !formState) {
    return null;
  }

  const fieldsToShow = getFieldsToShow(
    transformUpdateBookingFormStateToMinimalBooking(formState),
    Boolean(formState.customer?.isCashCustomer),
  );

  const fieldsToEnable = getFieldsToEdit(fieldsToShow, booking.editableFields);

  const showImportExportSection = isAnyVisible(fieldsToShow, [
    ColumnId.CustomsClassification,
    ColumnId.ImportReference,
    ColumnId.ExportReference,
  ]);

  const formDisabled = submitting || !hasAnyValidCustomer;

  return (
    <>
      <Box indent={3}>
        {formState.sailing ? (
          <SailingInfoHorizontal
            releasedAt={null}
            hasArrived={booking.hasArrived}
            sailing={formState.sailing}
            bookingStatus={booking.bookingStatus.status}
          />
        ) : (
          <NoSailingSelected />
        )}
      </Box>
      <Space />

      <BookingDetailsEditBanners booking={booking} formState={formState}>
        {children}
      </BookingDetailsEditBanners>

      <Cardy indent={[0, 3]} width={'100%'} borderRadius={16} gap>
        <BookingColumnGrid>
          <BookingColumn>
            <SailingDetailsSection
              fieldsToShow={fieldsToShow}
              fieldsToEnable={fieldsToEnable}
              disabled={formDisabled}
              formState={formState}
              hasArrived={booking.hasArrived}
            />
          </BookingColumn>
          <BookingColumn>
            <VehicleDetailsSection
              fieldsToShow={fieldsToShow}
              fieldsToEnable={fieldsToEnable}
              formState={formState}
              disabled={formDisabled}
            />
          </BookingColumn>

          <BookingColumn>
            <LoadingDetailsSection
              fieldsToShow={fieldsToShow}
              fieldsToEnable={fieldsToEnable}
              disabled={formDisabled}
              formState={formState}
              booking={booking}
            />
          </BookingColumn>
          <BookingColumn>
            {showImportExportSection && (
              <ImportExportDetailsSection
                fieldsToShow={fieldsToShow}
                fieldsToEnable={fieldsToEnable}
                disabled={formDisabled}
                booking={booking}
              />
            )}
          </BookingColumn>
        </BookingColumnGrid>
        <CargoGoodsDetailsSection
          fieldsToShow={fieldsToShow}
          fieldsToEnable={fieldsToEnable}
          disabled={formDisabled}
          booking={booking}
        />
      </Cardy>
    </>
  );
};
